import { cloneElement, useMemo, useContext, ReactElement } from 'react';
import {
    sanitizeListRestProps,
    Identifier,
    SortPayload,
    Exporter,
    useListContext,
    useResourceContext,
    useResourceDefinition,
    TopToolbar,
    CreateButton,
    FilterContext,
    FilterButton,
} from 'react-admin';
import { ToolbarProps } from '@mui/material';
import { ListExportButton } from './ListExportButton';

/**
 * Same as react-admin ListActions, but export uses {@link ListExportButton} so filtered exports
 * match the list query and use a high page size (see LIST_EXPORT_MAX_RESULTS).
 */
const StandardListActions = (props: StandardListActionsProps) => {
    const { className, filters: filtersProp, hasCreate: _, ...rest } = props;
    const {
        sort,
        displayedFilters,
        filterValues,
        exporter,
        showFilter,
        total,
    } = useListContext(props);
    const resource = useResourceContext(props);
    const { hasCreate } = useResourceDefinition(props);
    const filters = useContext(FilterContext) || filtersProp;
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional subset matching react-admin ListActions
    return useMemo(
        () => (
            <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
                {filtersProp
                    ? cloneElement(filtersProp, {
                          resource,
                          showFilter,
                          displayedFilters,
                          filterValues,
                          context: 'button',
                      })
                    : filters && <FilterButton />}
                {hasCreate && <CreateButton />}
                {exporter !== false && (
                    <ListExportButton disabled={total === 0} resource={resource} sort={sort} />
                )}
            </TopToolbar>
        ),
        /* eslint-disable react-hooks/exhaustive-deps */
        [
            resource,
            displayedFilters,
            filterValues,
            filtersProp,
            showFilter,
            filters,
            total,
            className,
            sort,
            exporter,
            hasCreate,
        ]
        /* eslint-enable react-hooks/exhaustive-deps */
    );
};

export interface StandardListActionsProps extends ToolbarProps {
    className?: string;
    filters?: ReactElement;
    displayedFilters?: Record<string, unknown>;
    exporter?: Exporter | boolean;
    filterValues?: Record<string, unknown>;
    permanentFilter?: Record<string, unknown>;
    hasCreate?: boolean;
    selectedIds?: Identifier[];
    onUnselectItems?: () => void;
    showFilter?: (filterName: string, defaultValue: unknown) => void;
    total?: number;
    sort?: SortPayload;
}

StandardListActions.defaultProps = {
    selectedIds: [],
    onUnselectItems: () => null,
};

export default StandardListActions;
